/**
 * TreeSelect Utility Functions
 * 
 * This utility provides common functions for handling treeselect data
 * across different components, following the patterns established in
 * the classify components.
 */

export interface TreeNode {
  key: string;
  label: string;
  value: string;
  data?: any;
  children?: TreeNode[];
}

/**
 * Get display name for category with proper fallback logic
 */
export function getCategoryDisplayName(category: any): string {
  // First try the name field
  if (category.name && category.name.trim()) {
    return category.name.trim();
  }
  
  // Fallback to code if name is empty
  if (category.code && category.code.trim()) {
    return category.code.trim();
  }
  
  // Final fallback with ID
  return `Category ${category.id || 'Unknown'}`;
}

/**
 * Convert category tree data to treeselect format
 * Handles both hierarchical and flat structures
 */
export function convertToTreeNodes(categories: any[]): TreeNode[] {
  if (!categories || categories.length === 0) {
    return [];
  }

  // Check if this is a hierarchical structure (from getTree API)
  if (isHierarchicalStructure(categories)) {
    return convertHierarchicalToTreeNodes(categories);
  }

  // Fall back to flat structure conversion (from getList2 API)
  return convertFlatToTreeNodes(categories);
}

/**
 * Check if the categories array has a hierarchical structure
 */
export function isHierarchicalStructure(categories: any[]): boolean {
  if (categories.length === 0) return false;
  const firstCategory = categories[0];
  return firstCategory && firstCategory.children !== undefined;
}

/**
 * Convert hierarchical structure to tree nodes
 */
export function convertHierarchicalToTreeNodes(categories: any[]): TreeNode[] {
  const convertNode = (category: any): TreeNode => {
    return {
      key: category.id?.toString() || '',
      label: getCategoryDisplayName(category),
      value: category.id?.toString() || '',
      children: category.children ? category.children.map((child: any) => convertNode(child)) : []
    };
  };

  return categories.map(convertNode);
}

/**
 * Convert flat structure to tree nodes with proper hierarchy
 */
export function convertFlatToTreeNodes(categories: any[]): TreeNode[] {
  // Create a map for quick lookup
  const categoryMap = new Map<number, TreeNode>();
  const rootNodes: TreeNode[] = [];

  // First pass: create all nodes
  categories.forEach((category) => {
    const node: TreeNode = {
      key: category.id?.toString() || '',
      label: getCategoryDisplayName(category),
      value: category.id?.toString() || '',
      children: []
    };
    categoryMap.set(category.id || 0, node);
  });

  // Second pass: build parent-child relationships
  categories.forEach((category) => {
    const node = categoryMap.get(category.id || 0);
    if (node) {
      if (!category.parentId || category.parentId === 0) {
        // Root node
        rootNodes.push(node);
      } else {
        // Child node
        const parentNode = categoryMap.get(category.parentId);
        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(node);
        } else {
          // Parent not found, treat as root
          rootNodes.push(node);
        }
      }
    }
  });

  return rootNodes;
}

/**
 * Recursively find a category option by value in the tree structure
 */
export function findCategoryOptionInTree(options: TreeNode[], value: string | number): TreeNode | null {
  for (const option of options) {
    if (option.value === value.toString() || option.key === value.toString()) {
      return option;
    }
    if (option.children && option.children.length > 0) {
      const found = findCategoryOptionInTree(option.children, value);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Extract category ID from treeselect value which can be either a string or an object
 */
export function extractCategoryId(categoryValue: any): string | number | null {
  if (!categoryValue) {
    return null;
  }
  
  // If it's an object with value property (from treeselect)
  if (typeof categoryValue === 'object' && categoryValue.value !== undefined) {
    return categoryValue.value;
  }
  
  // If it's a string or number (from loading existing data)
  if (typeof categoryValue === 'string' || typeof categoryValue === 'number') {
    return categoryValue;
  }
  
  return null;
}

/**
 * Extract category ID from treeselect value for search (supports multiple selection)
 */
export function extractCategoryIdFromTreeselect(categoryValue: any): string | string[] | null {
  if (!categoryValue) {
    return null;
  }
  
  // If it's an array (multiple selection)
  if (Array.isArray(categoryValue)) {
    return categoryValue.map(item => extractSingleCategoryId(item)).filter(id => id !== null);
  }
  
  // Single selection
  return extractSingleCategoryId(categoryValue);
}

/**
 * Extract single category ID from treeselect value
 */
export function extractSingleCategoryId(categoryValue: any): string | null {
  if (!categoryValue) {
    return null;
  }
  
  // If it's an object with value property (from treeselect)
  if (typeof categoryValue === 'object' && categoryValue.value !== undefined) {
    return categoryValue.value;
  }
  
  // If it's a string or number (from loading existing data)
  if (typeof categoryValue === 'string' || typeof categoryValue === 'number') {
    return categoryValue.toString();
  }
  
  return null;
}

/**
 * Filter out current category from tree options to prevent circular references
 */
export function filterOutCurrentCategory(treeOptions: TreeNode[], currentCategoryId: string): TreeNode[] {
  return treeOptions.filter(option => {
    // If this is the current category, exclude it
    if (option.value === currentCategoryId) {
      return false;
    }
    
    // Recursively filter children
    if (option.children && option.children.length > 0) {
      option.children = filterOutCurrentCategory(option.children, currentCategoryId);
    }
    
    return true;
  });
}

/**
 * Check if category options have changed to prevent unnecessary re-renders
 */
export function hasCategoryOptionsChanged(currentOptions: TreeNode[], newOptions: TreeNode[]): boolean {
  // Compare by length first
  if (currentOptions.length !== newOptions.length) {
    return true;
  }
  
  // Compare each option by key and label to avoid circular reference issues
  for (let i = 0; i < currentOptions.length; i++) {
    const current = currentOptions[i];
    const newOption = newOptions[i];
    
    if (current.key !== newOption.key || current.label !== newOption.label) {
      return true;
    }
    
    // Recursively compare children if they exist
    if (hasChildrenChanged(current.children, newOption.children)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if children arrays have changed
 */
export function hasChildrenChanged(currentChildren: TreeNode[] | undefined, newChildren: TreeNode[] | undefined): boolean {
  if (!currentChildren && !newChildren) return false;
  if (!currentChildren || !newChildren) return true;
  if (currentChildren.length !== newChildren.length) return true;
  
  for (let i = 0; i < currentChildren.length; i++) {
    const current = currentChildren[i];
    const newChild = newChildren[i];
    
    if (current.key !== newChild.key || current.label !== newChild.label) {
      return true;
    }
    
    // Recursively check nested children
    if (hasChildrenChanged(current.children, newChild.children)) {
      return true;
    }
  }
  
  return false;
}
